import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";

interface HostRow {
  hostId: string;
  hostname: string;
  displayName: string | null;
  status?: string;
  online?: boolean;
  clientLabel: string | null;
  siteLabel: string | null;
}

interface ModuleGrant {
  enabled?: boolean;
  allowDestructive?: boolean;
  tools?: string[];
}

interface CliScope {
  hosts: {
    hostIds: string[];
    clientIds: string[];
    siteIds: string[];
    tenantIds: string[];
  };
  modules: Record<string, ModuleGrant>;
}

interface CliClient {
  id: string;
  name: string;
  status: "online" | "stale" | "closed" | "revoked";
  scope: CliScope;
  connectedAt: string;
  lastSeenAt: string;
  closedAt: string | null;
  revokedAt: string | null;
  userAgent: string | null;
  remoteIp: string | null;
}

const MODULES = [
  { id: "powershell", label: "PowerShell" },
  { id: "files", label: "Fichiers" },
  { id: "process", label: "Processus" },
  { id: "services", label: "Services" },
  { id: "registry", label: "Registre" },
  { id: "eventviewer", label: "Events" },
  { id: "software", label: "Logiciels" },
  { id: "firewall", label: "Firewall" },
  { id: "cli", label: "CLI/terminaux" },
  { id: "computeruse", label: "Computer Use" },
  { id: "ssh", label: "SSH" },
  { id: "sql", label: "SQL" },
  { id: "ad", label: "AD" },
  { id: "dns", label: "DNS" },
  { id: "dhcp", label: "DHCP" },
  { id: "smb", label: "SMB" },
];

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} -> ${r.status}`);
  return r.json() as Promise<T>;
}

async function jsend(url: string, method: string, body?: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new Error((data.error as string) || `HTTP ${r.status}`);
  return data;
}

function normalizeScope(scope?: Partial<CliScope> | null): CliScope {
  return {
    hosts: {
      hostIds: Array.isArray(scope?.hosts?.hostIds) ? scope.hosts!.hostIds : [],
      clientIds: Array.isArray(scope?.hosts?.clientIds) ? scope.hosts!.clientIds : [],
      siteIds: Array.isArray(scope?.hosts?.siteIds) ? scope.hosts!.siteIds : [],
      tenantIds: Array.isArray(scope?.hosts?.tenantIds) ? scope.hosts!.tenantIds : [],
    },
    modules: scope?.modules && typeof scope.modules === "object" ? scope.modules : {},
  };
}

function statusText(client: CliClient) {
  if (client.revokedAt) return "révoqué";
  if (client.closedAt) return "fermé";
  if (client.status === "online") return "en ligne";
  return "inactif";
}

function relativeTime(value: string | null) {
  if (!value) return "-";
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  return `${Math.round(minutes / 60)}h`;
}

export function CliClientsConsolePage() {
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [hostIds, setHostIds] = createSignal<Set<string>>(new Set());
  const [modules, setModules] = createSignal<Record<string, ModuleGrant>>({});
  const [err, setErr] = createSignal<string | null>(null);
  const [saving, setSaving] = createSignal(false);

  const clientsQ = createQuery<CliClient[]>(() => ({
    queryKey: ["scoped-cli-clients"],
    queryFn: () => jget<{ clients: CliClient[] }>("/api/cli-clients").then((d) => d.clients || []),
    refetchInterval: 10_000,
  }));
  const hostsQ = createQuery<HostRow[]>(() => ({
    queryKey: ["scoped-cli-hosts"],
    queryFn: () => jget<{ items?: HostRow[] }>("/host/list").then((d) => d.items || []),
  }));

  const selected = createMemo(() => clientsQ.data?.find((c) => c.id === selectedId()) || clientsQ.data?.[0] || null);

  function loadClient(client: CliClient) {
    const scope = normalizeScope(client.scope);
    setSelectedId(client.id);
    setHostIds(new Set(scope.hosts.hostIds));
    setModules({ ...scope.modules });
    setErr(null);
  }

  createEffect(() => {
    const client = selected();
    if (client && !selectedId()) loadClient(client);
  });

  function toggleHost(id: string) {
    setHostIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleModule(id: string, enabled: boolean) {
    setModules((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), enabled } }));
  }

  function toggleDestructive(id: string, allowDestructive: boolean) {
    setModules((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), enabled: true, allowDestructive } }));
  }

  function buildScope(): CliScope {
    const activeModules: Record<string, ModuleGrant> = {};
    for (const [id, grant] of Object.entries(modules())) {
      if (grant.enabled !== false) {
        activeModules[id] = {
          enabled: true,
          allowDestructive: grant.allowDestructive === true,
          tools: Array.isArray(grant.tools) ? grant.tools : [],
        };
      }
    }
    return {
      hosts: { hostIds: Array.from(hostIds()), clientIds: [], siteIds: [], tenantIds: [] },
      modules: activeModules,
    };
  }

  async function saveScope() {
    const client = selected();
    if (!client) return;
    setSaving(true);
    setErr(null);
    try {
      await jsend(`/api/cli-clients/${client.id}/scope`, "PATCH", { scope: buildScope() });
      await clientsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function revoke(client: CliClient) {
    setErr(null);
    try {
      await jsend(`/api/cli-clients/${client.id}/revoke`, "POST");
      await clientsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div class="plugins-console">
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          CLI
          <span class="plugins-console-sub">clients temporaires</span>
        </span>
        <button class="btn btn--xs" type="button" onClick={() => clientsQ.refetch()}>
          Rafraîchir
        </button>
      </div>

      <Show when={err()}>
        <div class="bug-report-message bug-report-message--error">{err()}</div>
      </Show>

      <div
        style={{
          display: "grid",
          "grid-template-columns": "minmax(360px, 1fr) minmax(420px, 1.25fr)",
          gap: "14px",
          "min-height": "0",
          flex: "1 1 0",
        }}
      >
        <div class="rmm-table-wrap" style={{ "min-height": "0" }}>
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Statut</th>
                <th>Dernier signal</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <For each={clientsQ.data || []}>
                {(client) => (
                  <tr data-active={selected()?.id === client.id ? "true" : "false"} onClick={() => loadClient(client)}>
                    <td>
                      <span style={{ color: "var(--text-1)", "font-weight": 600 }}>{client.name}</span>
                      <br />
                      <span style={{ "font-family": "var(--font-mono)", "font-size": "10px", color: "var(--text-4)" }}>
                        {client.id}
                      </span>
                    </td>
                    <td>{statusText(client)}</td>
                    <td>{relativeTime(client.lastSeenAt)}</td>
                    <td>
                      <button
                        class="btn btn--xs"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void revoke(client);
                        }}
                      >
                        Révoquer
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>

        <Show when={selected()} fallback={<div class="empty-state">Aucun client CLI connecté.</div>}>
          {(client) => (
            <div class="settings-console" style={{ padding: "0", overflow: "auto" }}>
              <div class="settings-card">
                <h2>Droits de {client().name}</h2>
                <div class="settings-grid">
                  <label>
                    Identifiant
                    <input value={client().id} readOnly />
                  </label>
                  <label>
                    Dernier signal
                    <input value={relativeTime(client().lastSeenAt)} readOnly />
                  </label>
                </div>
              </div>

              <div class="settings-card">
                <h2>Hôtes</h2>
                <div style={{ display: "grid", gap: "6px", "max-height": "260px", overflow: "auto" }}>
                  <For each={hostsQ.data || []}>
                    {(host) => (
                      <label style={{ display: "flex", gap: "8px", "align-items": "center", "font-size": "13px" }}>
                        <input
                          type="checkbox"
                          checked={hostIds().has(host.hostId)}
                          onChange={() => toggleHost(host.hostId)}
                        />
                        <span style={{ "min-width": "0" }}>
                          <span style={{ color: "var(--text-1)" }}>
                            {host.displayName || host.hostname || host.hostId}
                          </span>
                          <span style={{ color: "var(--text-4)", "margin-left": "6px" }}>
                            {host.clientLabel || "-"} / {host.siteLabel || "-"}
                          </span>
                        </span>
                      </label>
                    )}
                  </For>
                </div>
              </div>

              <div class="settings-card">
                <h2>Modules</h2>
                <div
                  style={{
                    display: "grid",
                    "grid-template-columns": "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "10px",
                  }}
                >
                  <For each={MODULES}>
                    {(module) => {
                      const grant = () => modules()[module.id] || {};
                      const enabled = () => grant().enabled !== false && Boolean(modules()[module.id]);
                      return (
                        <div
                          style={{
                            border: "1px solid var(--border)",
                            "border-radius": "6px",
                            padding: "10px",
                            display: "grid",
                            gap: "8px",
                          }}
                        >
                          <label style={{ display: "flex", gap: "8px", "align-items": "center" }}>
                            <input
                              type="checkbox"
                              checked={enabled()}
                              onChange={(e) => toggleModule(module.id, e.currentTarget.checked)}
                            />
                            <span>{module.label}</span>
                          </label>
                          <label
                            style={{ display: "flex", gap: "8px", "align-items": "center", color: "var(--text-3)" }}
                          >
                            <input
                              type="checkbox"
                              disabled={!enabled()}
                              checked={grant().allowDestructive === true}
                              onChange={(e) => toggleDestructive(module.id, e.currentTarget.checked)}
                            />
                            <span>Écriture</span>
                          </label>
                        </div>
                      );
                    }}
                  </For>
                </div>
              </div>

              <div style={{ display: "flex", gap: "8px", "justify-content": "flex-end" }}>
                <button class="btn btn--primary" type="button" disabled={saving()} onClick={() => void saveScope()}>
                  Enregistrer
                </button>
              </div>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
}
